'use client';

import React, { useEffect, useRef, useState } from 'react';
import './Toast.scss';

export function Toast({
  message,
  type,
  onClose,
  duration = 3000,
  position,
}) {
  const [isPaused, setIsPaused] = useState(false);
  const [remainingTime, setRemainingTime] = useState(duration);

  const requestRef = useRef(null);
  const previousTimestampRef = useRef(null);

  useEffect(() => {
    function tick(timestamp) {
      if (!isPaused && previousTimestampRef.current !== null) {
        const dt = timestamp - previousTimestampRef.current;
        setRemainingTime((prev) => {
          const newVal = prev - dt;
          return newVal < 0 ? 0 : newVal;
        });
      }
      previousTimestampRef.current = timestamp;
      requestRef.current = requestAnimationFrame(tick);
    }

    requestRef.current = requestAnimationFrame(tick);

    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [isPaused]);

  useEffect(() => {
    if (remainingTime <= 0) {
      onClose();
    }
  }, [remainingTime, onClose]);

  const progress = (remainingTime / duration) * 100;
  const SWIPE_MAX = 200;
  const SWIPE_THRESHOLD = 100;
  const [translateX, setTranslateX] = useState(0);
  const startXRef = useRef(null);

  // Mouse events
  function handleMouseDown(e) {
    startXRef.current = e.clientX;
    window.addEventListener('mouseup', handleMouseUpGlobal);
  }

  function handleMouseUpGlobal(e) {
    if (startXRef.current !== null) {
      const diffX = e.clientX - startXRef.current;
      if (Math.abs(diffX) > SWIPE_THRESHOLD) {
        onClose();
      } else {
        setTranslateX(0);
      }
    }
    startXRef.current = null;
    window.removeEventListener('mouseup', handleMouseUpGlobal);
  }

  function handleMouseMove(e) {
    if (startXRef.current !== null) {
      const diffX = e.clientX - startXRef.current;
      const clamped = Math.max(-SWIPE_MAX, Math.min(diffX, SWIPE_MAX));
      setTranslateX(clamped);
    }
  }

  function handleMouseUp(e) {
    if (startXRef.current !== null) {
      const diffX = e.clientX - startXRef.current;
      if (Math.abs(diffX) > SWIPE_THRESHOLD) {
        onClose();
      } else {
        setTranslateX(0);
      }
      startXRef.current = null;
    }
  }

  // Touch events
  function handleTouchStart(e) {
    const touch = e.touches[0];
    startXRef.current = touch.clientX;
  }

  function handleTouchMove(e) {
    if (startXRef.current !== null) {
      const touch = e.touches[0];
      const diffX = touch.clientX - startXRef.current;
      const clamped = Math.max(-SWIPE_MAX, Math.min(diffX, SWIPE_MAX));
      setTranslateX(clamped);
    }
  }

  function handleTouchEnd(e) {
    if (startXRef.current !== null) {
      const diffX = e.changedTouches[0].clientX - startXRef.current;
      if (Math.abs(diffX) > SWIPE_THRESHOLD) {
        onClose();
      } else {
        setTranslateX(0);
      }
      startXRef.current = null;
    }
  }

  const distanceRatio = Math.abs(translateX) / SWIPE_MAX;
  const currentOpacity = 1 - distanceRatio;

  const positionClass = `toast-${position}`;
  const typeClass = `toast-${type}`;

  return (
    <div
      className={`toast ${typeClass} ${positionClass}`}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      style={{
        transform: `translateX(${translateX}px)`,
        opacity: currentOpacity,
        transition: startXRef.current
          ? 'none'
          : 'transform 0.3s ease, opacity 0.3s ease',
      }}
    >
      <span className="toast-message">{message}</span>
      <button className="toast-close" onClick={onClose}>
        &times;
      </button>
      <div className="toast-progress" style={{ width: `${progress}%` }} />
    </div>
  );
}
